import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { calculateCurrentPricing } from '@/utils/pricingCalculations';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';

export interface EstimatePricingChange {
  id: string;
  description: string;
  componentType: string;
  componentId: string;
  trade?: string;
  category?: string;
  oldPrice: number;
  newPrice: number;
  changeValue: number;
  changePercentage: number;
  quantity: number;
  oldTotal: number;
  newTotal: number;
  totalChangeValue: number;
}

export interface EstimatePricingChanges {
  changes: EstimatePricingChange[];
  totalOldAmount: number;
  totalNewAmount: number;
  totalChangeValue: number;
  totalChangePercentage: number;
  changesByTrade: Record<string, EstimatePricingChange[]>;
  changesByCategory: Record<string, EstimatePricingChange[]>;
}

interface UseEstimatePricingRefreshProps {
  estimateId: string;
  versionId?: string;
  companyId?: string;
}

export const useEstimatePricingRefresh = ({
  estimateId,
  versionId,
  companyId
}: UseEstimatePricingRefreshProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [pricingChanges, setPricingChanges] = useState<EstimatePricingChanges | null>(null);

  const analyzeVersionPricing = async (): Promise<EstimatePricingChanges | null> => {
    if (!estimateId) return null;

    setIsAnalyzing(true);
    try {
      // Get line items from version snapshot or current estimate
      let lineItemsData: any[] = [];
      
      if (versionId) {
        const { data: version, error: versionError } = await supabase
          .from('estimate_versions')
          .select('line_items_snapshot')
          .eq('id', versionId)
          .single();

        if (versionError) throw versionError;
        lineItemsData = Array.isArray(version.line_items_snapshot) ? version.line_items_snapshot : [];
      } else {
        const { data: lineItems, error: lineItemsError } = await supabase
          .from('estimate_line_items')
          .select('*')
          .eq('estimate_id', estimateId)
          .order('sort_order');

        if (lineItemsError) throw lineItemsError;
        
        lineItemsData = lineItems?.map(item => ({
          id: item.id,
          description: item.description,
          component_type: item.component_type,
          component_id: item.component_id,
          quantity: item.quantity_in_base_units,
          unit_cost: item.manual_override ? 
            (item.manual_unit_cost || item.unit_cost_snapshot) : 
            item.unit_cost_snapshot,
          total_cost: item.manual_override ? 
            (item.manual_total_cost || item.total_cost_snapshot) : 
            item.total_cost_snapshot
        })) || [];
      }

      let totalOldAmount = 0;
      let totalNewAmount = 0;

      // Get all unique component IDs first to batch fetch line item data
      const internalItemIds = lineItemsData
        .filter(item => item.component_type === 'internal_line_item')
        .map(item => item.component_id);
      
      const supplierItemIds = lineItemsData
        .filter(item => item.component_type === 'supplier_line_item')
        .map(item => item.component_id);

      // Batch fetch internal line items
      const internalItemsPromise = internalItemIds.length > 0 ? 
        supabase
          .from('internal_line_items')
          .select(`
            id,
            base_cost,
            category_id,
            trade,
            use_custom_tiers,
            override_markup,
            markup_source,
            custom_markup,
            markup_percentage
          `)
          .in('id', internalItemIds) : 
        Promise.resolve({ data: [], error: null });

      // Batch fetch supplier line items (now stored in internal_line_items)
      const supplierItemsPromise = supplierItemIds.length > 0 ?
        supabase
          .from('internal_line_items')
          .select(`
            id,
            base_cost,
            category_id,
            use_custom_tiers,
            override_markup,
            markup_source,
            custom_markup,
            markup_percentage
          `)
          .in('id', supplierItemIds) :
        Promise.resolve({ data: [], error: null });

      // Execute batch queries in parallel
      const [internalItemsResult, supplierItemsResult] = await Promise.all([
        internalItemsPromise,
        supplierItemsPromise
      ]);

      // Create maps for quick lookup
      const internalItemsMap = new Map(
        (internalItemsResult.data || []).map(item => [item.id, item])
      );
      const supplierItemsMap = new Map<string, any>(
        ((supplierItemsResult.data as any[]) || []).map((item: any) => [item.id, item])
      );

      // Get all unique category IDs to batch fetch category names
      const allCategoryIds = [
        ...(internalItemsResult.data || []).map(item => item.category_id),
        ...((supplierItemsResult.data as any[]) || []).map((item: any) => item.category_id)
      ].filter(Boolean);

      const uniqueCategoryIds = [...new Set(allCategoryIds)];
      
      // Batch fetch category names
      const categoriesPromise = uniqueCategoryIds.length > 0 ?
        supabase
          .from('supplier_categories')
          .select('id, name')
          .in('id', uniqueCategoryIds) :
        Promise.resolve({ data: [], error: null });

      const categoriesResult = await categoriesPromise;
      const categoriesMap = new Map(
        (categoriesResult.data || []).map(cat => [cat.id, cat.name])
      );

      // Calculate current pricing for all items in parallel
      const pricingPromises = lineItemsData.map(async (item) => {
        const oldUnitCost = parseFloat(item.unit_cost) || 0;
        const oldTotalCost = parseFloat(item.total_cost) || 0;
        const quantity = parseFloat(item.quantity) || 0;

        let newUnitCost = oldUnitCost;
        let trade = '';
        let category = '';

        if (item.component_type === 'internal_line_item') {
          const internalItem = internalItemsMap.get(item.component_id);
          
          if (internalItem) {
            trade = internalItem.trade || '';
            category = internalItem.category_id ? categoriesMap.get(internalItem.category_id) || '' : '';
            
            // Fetch custom markup tiers if line item uses them
            let customTiers: LineItemMarkupTier[] = [];
            const useCustomTiers = internalItem.use_custom_tiers && 
                                 internalItem.markup_source === 'custom';
            
            if (useCustomTiers) {
              const { data: markupTiers } = await supabase
                .from('line_item_markup_tiers')
                .select('*')
                .eq('line_item_id', item.component_id)
                .order('min_amount');
              
              customTiers = markupTiers || [];
            }
            
            // Determine the correct custom markup to use
            let customMarkupValue: number | undefined;
            if (internalItem.markup_source === 'custom' && !useCustomTiers) {
              customMarkupValue = internalItem.markup_percentage;
            } else if (internalItem.override_markup && internalItem.custom_markup) {
              customMarkupValue = internalItem.custom_markup;
            }
            
            const currentPricing = await calculateCurrentPricing(
              internalItem.base_cost,
              internalItem.category_id,
              companyId,
              customTiers,
              useCustomTiers,
              customMarkupValue
            );
            newUnitCost = currentPricing.finalPrice;
          }
        } else if (item.component_type === 'supplier_line_item') {
          const supplierItem = supplierItemsMap.get(item.component_id);
          
          if (supplierItem) {
            trade = ''; // Supplier line items don't have trade field
            category = supplierItem.category_id ? categoriesMap.get(supplierItem.category_id) || '' : '';
            
            // Fetch custom markup tiers if supplier line item uses them
            let customTiers: LineItemMarkupTier[] = [];
            const useCustomTiers = supplierItem.use_custom_tiers && 
                                 supplierItem.markup_source === 'custom';
            
            if (useCustomTiers) {
              const { data: markupTiers } = await supabase
                .from('line_item_markup_tiers')
                .select('*')
                .eq('line_item_id', item.component_id)
                .order('min_amount');
              
              customTiers = markupTiers || [];
            }
            
            // Determine the correct custom markup to use
            let customMarkupValue: number | undefined;
            if (supplierItem.markup_source === 'custom' && !useCustomTiers) {
              customMarkupValue = supplierItem.markup_percentage;
            } else if (supplierItem.override_markup && supplierItem.custom_markup) {
              customMarkupValue = supplierItem.custom_markup;
            }
            
            const currentPricing = await calculateCurrentPricing(
              supplierItem.base_cost,
              supplierItem.category_id,
              companyId,
              customTiers,
              useCustomTiers,
              customMarkupValue
            );
            newUnitCost = currentPricing.finalPrice;
          }
        }

        const newTotalCost = newUnitCost * quantity;
        const changeValue = newUnitCost - oldUnitCost;
        const totalChangeValue = newTotalCost - oldTotalCost;
        
        return {
          item,
          oldUnitCost,
          oldTotalCost,
          newUnitCost,
          newTotalCost,
          quantity,
          changeValue,
          totalChangeValue,
          trade,
          category
        };
      });

      // Wait for all pricing calculations to complete
      const pricingResults = await Promise.all(pricingPromises);

      // Process results and build changes array
      const pricingChanges: EstimatePricingChange[] = [];
      
      pricingResults.forEach(result => {
        const { item, oldUnitCost, oldTotalCost, newUnitCost, newTotalCost, quantity, changeValue, totalChangeValue, trade, category } = result;
        
        totalOldAmount += oldTotalCost;
        totalNewAmount += newTotalCost;

        // Check if there's a meaningful price change (more than 1 cent)
        if (Math.abs(changeValue) > 0.01) {
          const changePercentage = oldUnitCost > 0 ? (changeValue / oldUnitCost) * 100 : 0;
          
          pricingChanges.push({
            id: item.id,
            description: item.description,
            componentType: item.component_type,
            componentId: item.component_id,
            trade,
            category,
            oldPrice: oldUnitCost,
            newPrice: newUnitCost,
            changeValue,
            changePercentage,
            quantity,
            oldTotal: oldTotalCost,
            newTotal: newTotalCost,
            totalChangeValue
          });
        }
      });

      // Group changes by trade and category
      const changesByTrade: Record<string, EstimatePricingChange[]> = {};
      const changesByCategory: Record<string, EstimatePricingChange[]> = {};

      pricingChanges.forEach(change => {
        if (change.trade) {
          if (!changesByTrade[change.trade]) {
            changesByTrade[change.trade] = [];
          }
          changesByTrade[change.trade].push(change);
        }

        if (change.category) {
          if (!changesByCategory[change.category]) {
            changesByCategory[change.category] = [];
          }
          changesByCategory[change.category].push(change);
        }
      });

      const totalChangeValue = totalNewAmount - totalOldAmount;
      const totalChangePercentage = totalOldAmount > 0 ? (totalChangeValue / totalOldAmount) * 100 : 0;

      const result: EstimatePricingChanges = {
        changes: pricingChanges,
        totalOldAmount,
        totalNewAmount,
        totalChangeValue,
        totalChangePercentage,
        changesByTrade,
        changesByCategory
      };

      setPricingChanges(result);
      return result;

    } catch (error) {
      console.error('Error analyzing pricing changes:', error);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const hasChanges = useMemo(() => {
    return pricingChanges && pricingChanges.changes.length > 0;
  }, [pricingChanges]);

  return {
    analyzeVersionPricing,
    pricingChanges,
    hasChanges,
    isAnalyzing,
    clearPricingChanges: () => setPricingChanges(null)
  };
};
